import { CAD_MASTERPLAN_STYLE } from '../../Map/styles/mapTheme';
import {
  EMPTY_CAD_MASTERPLAN_COLLECTION,
  buildCadGreenTreeCollection,
  buildCadStreetCurbCollection,
  cadKindEqualsFilter,
} from './cadMasterplan';
import {
  CAD_ASPHALT_PATTERN_ID,
  CAD_GRASS_PATTERN_ID,
  cadTreeSpriteId,
  ensureCadMasterplanPatternImages,
} from './cadMasterplanPattern';
import {
  afterCadSymbolPlacement,
  bumpCadSymbolOpEpoch,
  cancelCadSymbolOp,
  isCadSymbolLayer,
  isCadSymbolOpEpoch,
  scheduleCadSymbolOp,
} from './afterCadSymbolPlacement';

export const CAD_SITE_LAYER_ID = 'cad-parcels-site';
export const CAD_STREET_SHADOW_LAYER_ID = 'cad-parcels-street-shadow';
export const CAD_STREET_LAYER_ID = 'cad-parcels-street';
export const CAD_CONTACT_SHADOW_LAYER_ID = 'cad-parcels-contact-shadow';
export const CAD_GUTTER_LAYER_ID = 'cad-parcels-gutter';
export const CAD_SIDEWALK_LAYER_ID = 'cad-parcels-sidewalk-ring';
export const CAD_GREEN_LAYER_ID = 'cad-parcels-green';
export const CAD_TREES_SHADOW_LAYER_ID = 'cad-green-trees-shadow';
export const CAD_TREES_LAYER_ID = 'cad-green-trees';
export const CAD_CURB_SOURCE_ID = 'cad-street-curbs';
export const CAD_TREES_SOURCE_ID = 'cad-green-trees';

export const CAD_TREE_LAYER_IDS = [
  CAD_TREES_SHADOW_LAYER_ID,
  CAD_TREES_LAYER_ID,
];

export const CAD_MASTERPLAN_LAYER_IDS = [
  CAD_SITE_LAYER_ID,
  CAD_STREET_SHADOW_LAYER_ID,
  CAD_STREET_LAYER_ID,
  CAD_CONTACT_SHADOW_LAYER_ID,
  CAD_GUTTER_LAYER_ID,
  CAD_SIDEWALK_LAYER_ID,
  CAD_GREEN_LAYER_ID,
  CAD_TREES_SHADOW_LAYER_ID,
  CAD_TREES_LAYER_ID,
];

export const CAD_OVERLAY_HIT_LAYER_IDS = [
  CAD_SITE_LAYER_ID,
  CAD_STREET_LAYER_ID,
  CAD_GREEN_LAYER_ID,
];

const CAD_MASTERPLAN_SOURCE_IDS = [
  CAD_TREES_SOURCE_ID,
  CAD_CURB_SOURCE_ID,
];

/** Capas/sources retiradas de extrusión: hay que borrarlas o el L sigue en el mapa. */
const CAD_RETIRED_LAYER_IDS = [
  'cad-parcels-sidewalk',
  'cad-parcels-sidewalk-fill',
  'cad-parcels-curb-band',
  'cad-parcels-curb',
  'cad-parcels-street-axis',
];
const CAD_RETIRED_SOURCE_IDS = ['cad-street-curb-bands', 'cad-street-axis'];

const TREE_RADIUS = [
  'interpolate', ['linear'], ['zoom'],
  15, [
    'match', ['get', 'tree_size'],
    'sm', 2.8,
    'lg', 4.6,
    3.6,
  ],
  17, [
    'match', ['get', 'tree_size'],
    'sm', 4.2,
    'lg', 7.2,
    5.6,
  ],
  19, [
    'match', ['get', 'tree_size'],
    'sm', 6.5,
    'lg', 11,
    8.5,
  ],
];

const TREE_SHADOW_RADIUS = [
  'interpolate', ['linear'], ['zoom'],
  15, 4.8,
  17, 7.4,
  19, 11.5,
];

/**
 * Cordones, bandas y árboles derivados del mismo array de features. El array
 * viene memoizado desde `decorateCadParcelsForMap`, así que alcanza con una
 * WeakMap para no recalcular geometría en cada toggle de stock o de hover.
 */
const derivedByFeatures = new WeakMap();
const previousLightByMap = new WeakMap();

const CAD_MASTERPLAN_DERIVED_VERSION = 15;

/**
 * El source GeoJSON de MapLibre simplifica con Douglas-Peucker (tolerance
 * 0.375) y tesela hasta zoom 18. En el loteo, a zoom medio eso recorta
 * vértices, el earcut deja triángulos y con el relleno translúcido se
 * ven como cuñas que desaparecen al acercar.
 */
export const CAD_GEOJSON_SOURCE_OPTIONS = {
  tolerance: 0,
  maxzoom: 24,
};

function emptyDerived() {
  return {
    curbs: EMPTY_CAD_MASTERPLAN_COLLECTION,
    trees: EMPTY_CAD_MASTERPLAN_COLLECTION,
  };
}

function getCadMasterplanDerived(features) {
  if (!Array.isArray(features) || !features.length) return emptyDerived();
  const cached = derivedByFeatures.get(features);
  if (cached && cached.version === CAD_MASTERPLAN_DERIVED_VERSION) return cached;
  const derived = {
    version: CAD_MASTERPLAN_DERIVED_VERSION,
    curbs: buildCadStreetCurbCollection(features),
    trees: buildCadGreenTreeCollection(features),
  };
  derivedByFeatures.set(features, derived);
  return derived;
}

/** Un solo sol para el loteo y los buildings 3D del satélite. */
function applyCadSunLight(map) {
  if (typeof map?.setLight !== 'function') return;
  try {
    if (!previousLightByMap.has(map)) {
      previousLightByMap.set(map, typeof map.getLight === 'function' ? map.getLight() : null);
    }
    map.setLight(CAD_MASTERPLAN_STYLE.sunLight);
  } catch (_) { /* estilo a medias */ }
}

function restoreMapLight(map) {
  if (typeof map?.setLight !== 'function' || !previousLightByMap.has(map)) return;
  const previous = previousLightByMap.get(map);
  previousLightByMap.delete(map);
  if (!previous) return;
  try { map.setLight(previous); } catch (_) { /* estilo a medias */ }
}

function setSourceData(map, sourceId, collection) {
  const source = map.getSource(sourceId);
  const data = collection?.features?.length ? collection : EMPTY_CAD_MASTERPLAN_COLLECTION;
  if (source && typeof source.setData === 'function') {
    source.setData(data);
    return;
  }
  map.addSource(sourceId, { type: 'geojson', data, ...CAD_GEOJSON_SOURCE_OPTIONS });
}

function addLayerBefore(map, layer, beforeId) {
  if (beforeId && map.getLayer(beforeId)) map.addLayer(layer, beforeId);
  else map.addLayer(layer);
}

/**
 * Antes de qué capa insertar el masterplan. El fill de lotes puede aparecer
 * después del primer ensure (y del schedule de sprites): hay que re-resolver.
 *
 * @param {import('maplibre-gl').Map} map
 * @param {string|undefined} preferredBeforeId
 * @param {string|undefined} lotFillLayerId
 * @returns {string|undefined}
 */
export function resolveMasterplanInsertBeforeId(map, preferredBeforeId, lotFillLayerId) {
  if (preferredBeforeId && map.getLayer(preferredBeforeId)) return preferredBeforeId;
  if (lotFillLayerId && map.getLayer(lotFillLayerId)) return lotFillLayerId;
  return undefined;
}

/**
 * Copas / sombra **encima** del fill y la línea de lotes (no se cortan en el
 * borde) y **debajo** de hover / selección / chips. El plantado exige holgura
 * al lote (~radio del sprite); no debería haber puntos sobre manzanas.
 *
 * Ids de chrome de lote (mismo contrato que `useCadParcelsLayer`): no
 * importamos el hook para no cerrar un ciclo.
 */
const CAD_LOT_CHROME_ABOVE_TREES = [
  'cad-parcels-hover',
  'cad-parcels-selected-glow',
  'cad-parcels-selected',
  'cad-parcels-label',
];

/**
 * @param {import('maplibre-gl').Map} map
 * @param {string[]} [aboveLotChromeBeforeIds]
 */
export function keepCadTreesAboveLots(map, aboveLotChromeBeforeIds = CAD_LOT_CHROME_ABOVE_TREES) {
  if (!map) return;
  const insertBefore = (Array.isArray(aboveLotChromeBeforeIds) ? aboveLotChromeBeforeIds : [])
    .find((id) => id && map.getLayer(id));
  const move = (layerId, beforeId) => {
    if (!map.getLayer(layerId)) return;
    try {
      if (beforeId && map.getLayer(beforeId)) map.moveLayer(layerId, beforeId);
      else map.moveLayer(layerId);
    } catch (_) { /* estilo a medias */ }
  };
  move(CAD_TREES_LAYER_ID, insertBefore);
  if (map.getLayer(CAD_TREES_LAYER_ID)) {
    move(CAD_TREES_SHADOW_LAYER_ID, CAD_TREES_LAYER_ID);
  } else {
    move(CAD_TREES_SHADOW_LAYER_ID, insertBefore);
  }
}

/** @deprecated Alias: las copas van encima de los lotes. */
export function keepCadTreesBelowLots(map, _insertBefore) {
  keepCadTreesAboveLots(map);
}

function dropLayer(map, layerId) {
  if (map.getLayer(layerId)) map.removeLayer(layerId);
}

function dropSource(map, sourceId) {
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

function dropRetiredCurbLayers(map) {
  CAD_RETIRED_LAYER_IDS.forEach((layerId) => dropLayer(map, layerId));
  CAD_RETIRED_SOURCE_IDS.forEach((sourceId) => dropSource(map, sourceId));
}

function setLayerVisibility(map, layerId, visible, immediate = false) {
  if (!map.getLayer(layerId)) return;
  const next = visible ? 'visible' : 'none';
  try {
    if (map.getLayoutProperty(layerId, 'visibility') === next) return;
  } catch (_) { /* unset = visible */ }
  const apply = () => {
    try { map.setLayoutProperty(layerId, 'visibility', next); } catch (_) { /* estilo a medias */ }
  };
  if (!immediate && isCadSymbolLayer(map, layerId)) afterCadSymbolPlacement(apply);
  else apply();
}

function applyMinZoom(map, layerId, minZoom) {
  if (!Number.isFinite(minZoom) || !map.getLayer(layerId)) return;
  try { map.setLayerZoomRange(layerId, minZoom, 24); } catch (_) { /* estilo a medias */ }
}

function treeCirclePaint(color, opacity, radius, extra = {}) {
  return {
    'circle-color': color,
    'circle-opacity': opacity,
    'circle-radius': radius,
    'circle-pitch-alignment': 'viewport',
    'circle-pitch-scale': 'viewport',
    ...extra,
  };
}

function treeIconImageExpression(variants) {
  if (variants <= 1) return cadTreeSpriteId(0);
  const expr = ['match', ['coalesce', ['get', 'tree_variant'], 0]];
  for (let i = 0; i < variants - 1; i += 1) expr.push(i, cadTreeSpriteId(i));
  expr.push(cadTreeSpriteId(variants - 1));
  return expr;
}

function treeIconSizeExpression(style) {
  const factors = style.treeIconSizeByTreeSize || {};
  const expr = ['interpolate', ['linear'], ['zoom']];
  (style.treeIconSizeStops || []).forEach(([zoom, base]) => {
    expr.push(zoom, [
      'match', ['get', 'tree_size'],
      'sm', base * (factors.sm ?? 1),
      'lg', base * (factors.lg ?? 1),
      base * (factors.md ?? 1),
    ]);
  });
  return expr;
}

function setLayerLayout(map, layerId, layout) {
  if (!map.getLayer(layerId)) return;
  Object.entries(layout).forEach(([key, value]) => {
    try { map.setLayoutProperty(layerId, key, value); } catch (_) { /* estilo a medias */ }
  });
}

function setLayerPaint(map, layerId, paint) {
  if (!map.getLayer(layerId)) return;
  Object.entries(paint).forEach(([key, value]) => {
    try { map.setPaintProperty(layerId, key, value); } catch (_) { /* estilo a medias */ }
  });
}

/**
 * Capa CAD más baja (debajo va satélite / PNG / buildings).
 *
 * @param {import('maplibre-gl').Map|null} map
 * @param {string} [lotFillLayerId]
 * @returns {string|undefined}
 */
export function resolveCadOverlayInsertBeforeId(map, lotFillLayerId) {
  if (!map) return undefined;
  const ids = [CAD_SITE_LAYER_ID, CAD_STREET_LAYER_ID, CAD_GREEN_LAYER_ID, lotFillLayerId];
  return ids.find((id) => id && map.getLayer(id));
}

/**
 * Capas de contexto (site / street / green) para query de “click en el loteo”.
 *
 * @param {import('maplibre-gl').Map|null} map
 * @param {string[]} extraLayerIds
 * @returns {string[]}
 */
export function cadOverlayHitLayerIds(map, extraLayerIds = []) {
  if (!map) return [];
  return [...CAD_OVERLAY_HIT_LAYER_IDS, ...extraLayerIds].filter((id) => map.getLayer(id));
}

/**
 * Click sobre el loteo (lotes o contexto). No abre catastro.
 *
 * @param {import('maplibre-gl').Map|null} map
 * @param {{ x: number, y: number }|null|undefined} point
 * @param {string[]} [extraLayerIds]
 * @returns {object[]}
 */
export function queryCadOverlayAtPoint(map, point, extraLayerIds = []) {
  const layers = cadOverlayHitLayerIds(map, extraLayerIds);
  if (!map || !point || !layers.length) return [];
  try {
    return map.queryRenderedFeatures(point, { layers });
  } catch {
    return [];
  }
}

/**
 * @param {import('maplibre-gl').Map|null} map
 * @param {{ extraSymbolIds?: string[], extraSources?: string[] }} [options]
 */
export function removeCadMasterplanLayers(map, options = {}) {
  if (!map) return;
  const extraSymbolIds = Array.isArray(options.extraSymbolIds) ? options.extraSymbolIds : [];
  const extraSources = Array.isArray(options.extraSources) ? options.extraSources : [];
  const epoch = bumpCadSymbolOpEpoch();
  dropRetiredCurbLayers(map);

  // Un add en vuelo repondría el symbol justo después del teardown.
  [CAD_TREES_LAYER_ID, ...extraSymbolIds].forEach((layerId) => cancelCadSymbolOp(layerId));

  const layerIds = [...extraSymbolIds, ...CAD_MASTERPLAN_LAYER_IDS.slice().reverse()];
  layerIds.forEach((layerId) => {
    if (isCadSymbolLayer(map, layerId)) return;
    dropLayer(map, layerId);
  });

  afterCadSymbolPlacement(() => {
    // Un repaint en el medio ya rearmó el overlay: no lo desarmes.
    if (!isCadSymbolOpEpoch(epoch)) return;
    layerIds.forEach((layerId) => dropLayer(map, layerId));
    [...extraSources, ...CAD_MASTERPLAN_SOURCE_IDS].forEach((sourceId) => {
      dropSource(map, sourceId);
    });
    restoreMapLight(map);
  });
}

/**
 * Site → street → sombra → vereda (línea ancha en el anillo) → green → árboles,
 * debajo de los lotes. En align se ocultan: el loteo entero sigue siendo guía
 * de contorno.
 *
 * @param {import('maplibre-gl').Map} map
 * @param {GeoJSON.FeatureCollection} geojson
 * @param {{ paintMode?: string, minZoom?: number, beforeId?: string, lotFillLayerId?: string, parcelsSourceId: string, contextEnabled?: boolean }} options
 */
export function ensureCadMasterplanLayers(map, geojson, options) {
  const {
    paintMode = 'stock',
    minZoom,
    beforeId,
    lotFillLayerId,
    parcelsSourceId,
    contextEnabled = true,
  } = options;
  if (!map || !parcelsSourceId) return;

  dropRetiredCurbLayers(map);

  const style = CAD_MASTERPLAN_STYLE;
  const features = Array.isArray(geojson?.features) ? geojson.features : [];
  const patterns = ensureCadMasterplanPatternImages(map);
  const derived = getCadMasterplanDerived(features);
  setSourceData(map, CAD_CURB_SOURCE_ID, derived.curbs);
  setSourceData(map, CAD_TREES_SOURCE_ID, derived.trees);

  const visible = paintMode !== 'align' && contextEnabled !== false;
  if (visible) applyCadSunLight(map);
  else restoreMapLight(map);

  if (!map.getLayer(CAD_SITE_LAYER_ID)) {
    addLayerBefore(map, {
      id: CAD_SITE_LAYER_ID,
      type: 'fill',
      source: parcelsSourceId,
      filter: cadKindEqualsFilter('site'),
      paint: {
        'fill-antialias': false,
        'fill-color': style.siteFill,
        'fill-opacity': style.siteFillOpacity,
        'fill-outline-color': style.siteLine,
      },
    }, beforeId);
  }

  if (!map.getLayer(CAD_STREET_SHADOW_LAYER_ID)) {
    addLayerBefore(map, {
      id: CAD_STREET_SHADOW_LAYER_ID,
      type: 'fill',
      source: parcelsSourceId,
      filter: cadKindEqualsFilter('street'),
      paint: {
        'fill-antialias': false,
        'fill-color': style.streetShadowFill,
        'fill-opacity': style.streetShadowOpacity,
        'fill-translate': style.streetShadowTranslate,
      },
    }, beforeId);
  }

  if (!map.getLayer(CAD_STREET_LAYER_ID)) {
    const streetPaint = patterns.asphalt
      ? {
        'fill-antialias': false,
        'fill-color': style.streetFill,
        'fill-pattern': CAD_ASPHALT_PATTERN_ID,
        'fill-opacity': style.streetFillOpacity,
      }
      : {
        'fill-antialias': false,
        'fill-color': style.streetFill,
        'fill-opacity': style.streetFillOpacity,
      };
    addLayerBefore(map, {
      id: CAD_STREET_LAYER_ID,
      type: 'fill',
      source: parcelsSourceId,
      filter: cadKindEqualsFilter('street'),
      paint: streetPaint,
    }, beforeId);
  }

  if (!map.getLayer(CAD_CONTACT_SHADOW_LAYER_ID)) {
    addLayerBefore(map, {
      id: CAD_CONTACT_SHADOW_LAYER_ID,
      type: 'line',
      source: CAD_CURB_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': style.contactShadowColor,
        'line-opacity': style.contactShadowOpacity,
        'line-width': style.contactShadowWidth,
        'line-blur': style.contactShadowBlur,
        'line-offset': style.contactShadowLineOffset,
      },
    }, beforeId);
  }

  if (!map.getLayer(CAD_GUTTER_LAYER_ID)) {
    addLayerBefore(map, {
      id: CAD_GUTTER_LAYER_ID,
      type: 'line',
      source: CAD_CURB_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': style.gutterColor,
        'line-opacity': style.gutterOpacity,
        'line-width': style.gutterWidth,
        'line-offset': style.gutterLineOffset,
      },
    }, beforeId);
  }

  if (!map.getLayer(CAD_SIDEWALK_LAYER_ID)) {
    addLayerBefore(map, {
      id: CAD_SIDEWALK_LAYER_ID,
      type: 'line',
      source: CAD_CURB_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round', 'line-miter-limit': 4 },
      paint: {
        'line-color': style.sidewalkColor,
        'line-opacity': style.sidewalkOpacity,
        'line-width': style.sidewalkWidth,
        'line-offset': 0,
      },
    }, beforeId);
  }

  const greenPaint = patterns.grass
    ? {
      'fill-antialias': false,
      'fill-color': style.greenFill,
      'fill-pattern': CAD_GRASS_PATTERN_ID,
      'fill-opacity': style.greenFillOpacity,
    }
    : {
      'fill-antialias': false,
      'fill-color': style.greenFill,
      'fill-opacity': style.greenFillOpacity,
    };

  if (!map.getLayer(CAD_GREEN_LAYER_ID)) {
    addLayerBefore(map, {
      id: CAD_GREEN_LAYER_ID,
      type: 'fill',
      source: parcelsSourceId,
      filter: cadKindEqualsFilter('green'),
      paint: greenPaint,
    }, beforeId);
  }

  const treeShadowPaint = treeCirclePaint(
    style.treeShadow,
    style.treeShadowOpacity,
    TREE_SHADOW_RADIUS,
    {
      'circle-translate': style.treeShadowTranslate,
      'circle-translate-anchor': 'map',
    },
  );
  const treePaint = patterns.trees
    ? { 'icon-opacity': style.treeCanopyOpacity }
    : treeCirclePaint(
      style.treeCanopy,
      style.treeCanopyOpacity,
      TREE_RADIUS,
      {
        'circle-stroke-color': style.treeStroke,
        'circle-stroke-width': 0.6,
        'circle-stroke-opacity': 0.35,
      },
    );

  if (!map.getLayer(CAD_TREES_SHADOW_LAYER_ID)) {
    addLayerBefore(map, {
      id: CAD_TREES_SHADOW_LAYER_ID,
      type: 'circle',
      source: CAD_TREES_SOURCE_ID,
      minzoom: style.treeMinZoom,
      paint: treeShadowPaint,
    }, beforeId);
  }

  // Las copas son sprites recién cuando hay canvas. Sacar y reponer la capa
  // en la misma op evita quedar con el círculo hasta el próximo repaint.
  const treeType = patterns.trees ? 'symbol' : 'circle';
  const currentTreeType = map.getLayer(CAD_TREES_LAYER_ID)?.type;
  if (currentTreeType !== treeType) {
    const treeLayer = patterns.trees
      ? {
        id: CAD_TREES_LAYER_ID,
        type: 'symbol',
        source: CAD_TREES_SOURCE_ID,
        minzoom: style.treeMinZoom,
        layout: {
          'icon-image': treeIconImageExpression(style.treeVariants),
          'icon-size': treeIconSizeExpression(style),
          // Billboard: el pitch no estira la copa. MapLibre igual arma
          // LayerPlacement: addLayer va después del frame de render.
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-pitch-alignment': 'viewport',
          'icon-rotation-alignment': 'viewport',
        },
        paint: treePaint,
      }
      : {
        id: CAD_TREES_LAYER_ID,
        type: 'circle',
        source: CAD_TREES_SOURCE_ID,
        minzoom: style.treeMinZoom,
        paint: treePaint,
      };
    const swapTrees = () => {
      if (!map.getSource(CAD_TREES_SOURCE_ID)) return;
      // El fill de lotes se agrega justo después del primer ensure; el
      // beforeId del closure suele ser undefined en MapPage (overlay).
      const insertBefore = resolveMasterplanInsertBeforeId(map, beforeId, lotFillLayerId);
      if (map.getLayer(CAD_TREES_LAYER_ID)?.type !== treeType) {
        dropLayer(map, CAD_TREES_LAYER_ID);
        addLayerBefore(map, treeLayer, insertBefore);
      }
      keepCadTreesAboveLots(map);
      setLayerPaint(map, CAD_TREES_LAYER_ID, treePaint);
      setLayerVisibility(map, CAD_TREES_LAYER_ID, visible, true);
      applyMinZoom(map, CAD_TREES_LAYER_ID, Math.max(
        style.treeMinZoom,
        Number.isFinite(minZoom) ? minZoom : style.treeMinZoom,
      ));
    };
    if (treeType === 'symbol' || currentTreeType === 'symbol') {
      scheduleCadSymbolOp(CAD_TREES_LAYER_ID, swapTrees);
    } else {
      swapTrees();
    }
  }

  setLayerPaint(map, CAD_SITE_LAYER_ID, {
    'fill-antialias': false,
    'fill-color': style.siteFill,
    'fill-opacity': style.siteFillOpacity,
  });
  setLayerPaint(map, CAD_STREET_SHADOW_LAYER_ID, {
    'fill-antialias': false,
    'fill-color': style.streetShadowFill,
    'fill-opacity': style.streetShadowOpacity,
    'fill-translate': style.streetShadowTranslate,
  });
  setLayerPaint(map, CAD_STREET_LAYER_ID, {
    'fill-antialias': false,
    'fill-color': style.streetFill,
    'fill-opacity': style.streetFillOpacity,
  });
  setLayerPaint(map, CAD_CONTACT_SHADOW_LAYER_ID, {
    'line-color': style.contactShadowColor,
    'line-opacity': style.contactShadowOpacity,
    'line-width': style.contactShadowWidth,
    'line-blur': style.contactShadowBlur,
    'line-offset': style.contactShadowLineOffset,
  });
  setLayerPaint(map, CAD_GUTTER_LAYER_ID, {
    'line-color': style.gutterColor,
    'line-opacity': style.gutterOpacity,
    'line-width': style.gutterWidth,
    'line-offset': style.gutterLineOffset,
  });
  setLayerLayout(map, CAD_SIDEWALK_LAYER_ID, {
    'line-join': 'round',
    'line-cap': 'round',
  });
  setLayerPaint(map, CAD_SIDEWALK_LAYER_ID, {
    'line-color': style.sidewalkColor,
    'line-opacity': style.sidewalkOpacity,
    'line-width': style.sidewalkWidth,
    'line-offset': 0,
  });
  setLayerPaint(map, CAD_GREEN_LAYER_ID, greenPaint);
  if (!patterns.grass) {
    try {
      if (map.getLayer(CAD_GREEN_LAYER_ID)) {
        map.setPaintProperty(CAD_GREEN_LAYER_ID, 'fill-pattern', undefined);
      }
    } catch (_) { /* MapLibre puede no aceptar unset */ }
  }
  setLayerPaint(map, CAD_TREES_SHADOW_LAYER_ID, treeShadowPaint);
  setLayerPaint(map, CAD_TREES_LAYER_ID, treePaint);

  CAD_MASTERPLAN_LAYER_IDS.forEach((layerId) => {
    setLayerVisibility(map, layerId, visible);
    if (CAD_TREE_LAYER_IDS.includes(layerId)) {
      applyMinZoom(map, layerId, Math.max(
        style.treeMinZoom,
        Number.isFinite(minZoom) ? minZoom : style.treeMinZoom,
      ));
      return;
    }
    applyMinZoom(map, layerId, minZoom);
  });

  keepCadTreesAboveLots(map);
}
